import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ChevronDown, ChevronRight, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// Per-carrier rollup of Owner Operator settlements -- the O/O counterpart
// to Driver Pay Report, useful for 1099 payment totals at a glance.
export default function CarrierPayReport() {
  const [settlements, setSettlements] = useState([]);
  const [loads, setLoads] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Settlement.list('-settlement_date', 500),
      client.entities.Load.list('-delivery_date', 500),
      client.entities.DriverProfile.list('full_name', 200),
    ]).then(([s, l, d]) => { setSettlements(s); setLoads(l); setDriverProfiles(d); setLoading(false); });
  }, []);

  const ownerOpEmails = new Set(driverProfiles.filter(d => d.driver_type === 'Owner Operator').map(d => d.user_email));

  const filtered = settlements.filter(s =>
    inRange(s.settlement_date || s.finalized_date || s.created_date, start, end) &&
    ownerOpEmails.has(s.driver_email)
  );

  const byCarrier = filtered.reduce((acc, s) => {
    const key = s.driver_email || s.driver_name;
    if (!acc[key]) acc[key] = { name: s.driver_name, email: s.driver_email, records: [] };
    acc[key].records.push(s);
    return acc;
  }, {});

  const carrierRows = Object.values(byCarrier).map(c => {
    const gross = c.records.reduce((s, r) => s + (r.gross_revenue || 0), 0);
    const net = c.records.reduce((s, r) => s + (r.final_net_pay ?? r.net_driver_pay ?? 0), 0);
    // Was missing fuel advances and lumper/late fees -- Deductions didn't
    // add up to Gross - Net whenever any of those applied (Net Pay itself
    // was always correct, since it comes straight from the settlement).
    const deductions = c.records.reduce((s, r) => s + (r.carrier_take_amount || 0) + (r.dispatch_fee_amount || 0) + (r.fuel_advance_total || 0) + (r.lumper_fee_deducted || 0) + (r.late_fee_deducted || 0), 0);
    return { ...c, gross, net, deductions };
  });

  const csvRows = carrierRows.map(c => ({
    Carrier: c.name,
    'Loads Count': c.records.length,
    'Gross Revenue': c.gross.toFixed(2),
    Deductions: c.deductions.toFixed(2),
    'Net Pay': c.net.toFixed(2),
  }));

  const handlePrint = () => {
    const cols = ['Carrier', 'Loads', 'Gross Revenue', 'Deductions', 'Net Pay'];
    const dataRows = carrierRows.map(c => [
      { value: c.name, class: 'bold' }, c.records.length,
      { value: `$${c.gross.toLocaleString()}`, class: 'positive' },
      { value: `-$${c.deductions.toFixed(2)}`, class: 'negative' },
      { value: `$${c.net.toFixed(2)}`, class: 'positive' },
    ]);
    const grandGross = carrierRows.reduce((s, c) => s + c.gross, 0);
    const grandNet = carrierRows.reduce((s, c) => s + c.net, 0);
    const grandLoads = carrierRows.reduce((s, c) => s + c.records.length, 0);
    const totalsRow = ['TOTALS', grandLoads, { value: `$${grandGross.toLocaleString()}`, class: 'gold' }, '', { value: `$${grandNet.toFixed(2)}`, class: 'gold' }];
    const cards = [
      { label: 'Carriers', value: carrierRows.length, color: 'gold' },
      { label: 'Total Loads', value: grandLoads, color: '' },
      { label: 'Gross Revenue', value: `$${grandGross.toLocaleString()}`, color: 'positive' },
      { label: 'Total Net Pay', value: `$${grandNet.toFixed(2)}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Carrier Pay Report (Owner Operators)', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'carrier-pay-report.csv')} exportDisabled={loading || carrierRows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['', 'Carrier', 'Loads', 'Gross Revenue', 'Deductions', 'Net Pay'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {carrierRows.map(c => (
              <>
                <tr key={c.email} className="hover:bg-white/3 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [c.email]: !p[c.email] }))}>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {expanded[c.email] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </td>
                  <td className="px-3 py-2.5 font-semibold text-foreground">{c.name}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{c.records.length}</td>
                  <td className="px-3 py-2.5 font-semibold text-foreground">${c.gross.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-red-400">−${c.deductions.toFixed(2)}</td>
                  <td className="px-3 py-2.5 font-bold text-green-400">${c.net.toFixed(2)}</td>
                </tr>
                {expanded[c.email] && c.records.map(r => {
                  const l = loads.find(lo => lo.id === r.load_id);
                  return (
                    <tr key={r.id} className="bg-white/2">
                      <td></td>
                      <td className="px-3 py-2 pl-8 text-muted-foreground">└ #{r.load_number}</td>
                      <td className="px-3 py-2 text-muted-foreground">{l?.delivery_date || '—'}</td>
                      <td className="px-3 py-2 text-foreground">${(r.gross_revenue || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-red-400">−${((r.carrier_take_amount || 0) + (r.dispatch_fee_amount || 0) + (r.fuel_advance_total || 0) + (r.lumper_fee_deducted || 0) + (r.late_fee_deducted || 0)).toFixed(2)}</td>
                      <td className="px-3 py-2 text-green-400">${(r.final_net_pay ?? r.net_driver_pay ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </>
            ))}
          </tbody>
        </table>
        {carrierRows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
